import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { stripNonDigits } from '../../utils/validators';
import { authAPI } from '../../services/auth-api.service';

interface MFAFormProps {
  onBack?: () => void;
  onSuccess?: (user: any, token: string) => void;
  email?: string;
  onShowToast?: (message: string) => void;
}

export const MFAForm: React.FC<MFAFormProps> = ({
  onBack,
  onSuccess,
  email,
  onShowToast,
}) => {
  const {
    isLoading,
    error,
    verifyOTP,
    resendOTP,
    goToLogin,
    clearAuthError,
  } = useAuth();

  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpError, setOtpError] = useState('');

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(timer => timer - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpError('');

    try {
      console.log('==========================================');
      console.log('[MFAForm] Step 1: Calling OTP validation API...');
      console.log('[MFAForm] OTP:', otp);
      console.log('[MFAForm] Email:', email);

      const result = await verifyOTP({
        otp: otp,
        email: email || '',
      });

      if (result.type === 'auth/verifyOTP/fulfilled') {
        const payload = result.payload as any;

        // STEP 2: Extract token from OTP validation response (matching Angular)
        const token = payload.data?.token || payload.token || payload.access_token;

        if (!token || token.trim().length === 0) {
          throw new Error('No token received from OTP validation API');
        }

        console.log('[MFAForm] Step 2: Token received from OTP validation');
        console.log('[MFAForm] Token preview:', token.substring(0, 30) + '...');
        console.log('[MFAForm] Token length:', token.length);

        // STEP 3: Store token in sessionStorage (matching Angular)
        authAPI.storeAuthToken(token);

        // Verify token was stored
        const storedToken = sessionStorage.getItem('finpro_k_Authorization');
        console.log('[MFAForm] Token stored successfully:', !!storedToken);
        console.log('[MFAForm] Stored token matches:', storedToken === token);

        // STEP 4: Pass token to Angular Bridge (matching Angular flow)
        // Angular Bridge will call getUserInfo and handle navigation
        console.log('[MFAForm] Step 3: Calling Angular Bridge with token');
        console.log('[MFAForm] Angular will call getUserInfo API and handle navigation');

        if ((window as any).AngularAuthBridge?.handlePostLoginWithToken) {
          console.log('[MFAForm] Calling AngularAuthBridge.handlePostLoginWithToken');
          (window as any).AngularAuthBridge.handlePostLoginWithToken(token);
        } else {
          console.error('[MFAForm] ❌ AngularAuthBridge not found on window object!');
          throw new Error('Navigation service not available. Please refresh the page.');
        }

        // Notify parent component (optional)
        onSuccess?.(null, token);

        console.log('[MFAForm] ✅ MFA login flow completed successfully');
        console.log('==========================================');
      }
    } catch (error: any) {
      console.error('[MFAForm] ❌ OTP verification failed:', error);
      console.error('[MFAForm] Error details:', {
        message: error.message,
        error: error,
      });

      const errorMessage = error.response?.data?.message || error.data?.message || error.message || 'OTP verification failed. Please try again.';
      setOtpError(errorMessage);

      console.log('==========================================');
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    try {
      await resendOTP();
      setResendCooldown(60); // 60-second cooldown matching Angular
      setOtpError('');
      onShowToast?.('OTP resent successfully');
      console.log('[MFAForm] OTP resent successfully');
    } catch (error) {
      console.error('[MFAForm] Failed to resend OTP:', error);
      setOtpError('Failed to resend OTP. Please try again.');
    }
  };

  const handleBack = useCallback(() => {
    goToLogin();
    onBack?.();
  }, [goToLogin, onBack]);

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <p className="text-black font-medium">
          Please enter the 6-digit OTP sent to{" "}
          <span className="font-bold text-black">{email || 'your email'}</span>
        </p>
      </div>

      {/* Form - Full Height */}
      <div className="flex-1 flex flex-col min-h-0">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          {/* Form Fields Section - Top */}
          <div className="space-y-6 mt-8">
            <div>
              <label htmlFor="otp" className="block text-lg font-bold mb-2" style={{ color: '#0F3340' }}>
                OTP
              </label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => {
                  const value = stripNonDigits(e.target.value).slice(0, 6);
                  setOtp(value);
                  if (otpError) setOtpError('');
                  if (error) clearAuthError();
                }}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="w-full px-4 py-3 text-left text-lg tracking-wider rounded-lg border border-[#D9F0FF] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B84C6] focus:border-[#1B84C6]"
                autoComplete="one-time-code"
                required
              />
              {/* Resend Timer/Link - Below input, right-aligned */}
              <div className="mt-2 text-right text-xs">
                {resendCooldown > 0 ? (
                  <span className="text-gray-500 whitespace-nowrap">
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <span
                    onClick={handleResendOTP}
                    className="text-black underline cursor-pointer hover:text-gray-700 whitespace-nowrap"
                  >
                    RESEND OTP
                  </span>
                )}
              </div>
            </div>

            {/* Error Message - Plain Text Below OTP Field */}
            {(otpError || error) && (
              <div className="text-sm" style={{ color: '#CA7373' }}>
                {otpError || error}
              </div>
            )}
          </div>

          {/* Spacer - pushes button to bottom */}
          <div className="flex-1"></div>

          {/* Button Section - Absolute Bottom */}
          <div className="pb-4">
            <Button
              type="submit"
              loading={isLoading}
              disabled={otp.length !== 6}
              fullWidth
              size="lg"
              variant="gradient"
            >
              Verify Code
            </Button>

            {/* Back to Sign In Link */}
            <div className="mt-8 text-center text-sm">
              <div className="text-gray-600">
                <span
                  onClick={handleBack}
                  className="text-sm font-semibold cursor-pointer flex items-center justify-center"
                  style={{ color: '#0074A5' }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};